/**
 * components/landing/home/architecture/ArchitectureCanvas.jsx — the SVG
 * that powers the Platform Architecture engine.
 *
 * Pure-SVG (no WebGL) so it works everywhere, respects reduced-motion,
 * and stays under 30kb on the wire.  Handles:
 *   • rotating outer decorative ring (very subtle)
 *   • pulsing core with orbiting particle
 *   • connection edges with flowing dash animation
 *   • data-packet circles that ride along the edges via <animateMotion>
 *   • 18 module nodes on two concentric rings
 *   • hover (highlight related + dim others)
 *   • click (fire onSelect + parent handles panel)
 *   • view-mode dimming (fade out modules outside the highlight set)
 *
 * Props:
 *   viewMode           active view-mode object (see data.js)
 *   activeId           currently-selected module id (or null)
 *   hoverId            currently-hovered id (or null)
 *   onSelect(id)       fired on node click / keyboard Enter
 *   onHover(id | null) fired on node pointer-enter / leave
 *   reducedMotion      boolean — skip continuous animations if true
 */

import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  Bot, LineChart, Wand2, FlaskConical, Wallet, BarChart3, ScanLine,
  GraduationCap, PieChart, BookOpen, Workflow, CreditCard, UsersRound,
  HandCoins, Building2, Network, ShieldCheck, BellRing, Sparkles,
} from 'lucide-react'

import { MODULES, VIEW_MODES, TONE_COLORS, GEOMETRY, relatedIds, buildEdges, getModule } from './data'

// ── Icon lookup ────────────────────────────────────────────────────────
const ICONS = {
  Bot, LineChart, Wand2, FlaskConical, Wallet, BarChart3, ScanLine,
  GraduationCap, PieChart, BookOpen, Workflow, CreditCard, UsersRound,
  HandCoins, Building2, Network, ShieldCheck, BellRing,
}

// ── Geometry helpers ───────────────────────────────────────────────────
const CENTER = GEOMETRY.center
const R_INNER = GEOMETRY.innerRingRadius
const R_OUTER = GEOMETRY.outerRingRadius

// polar → cartesian on our SVG (angle in degrees, 0° = top, CW positive)
const polar = (angleDeg, radius) => {
  const a = (angleDeg - 90) * (Math.PI / 180)
  return { x: CENTER + Math.cos(a) * radius, y: CENTER + Math.sin(a) * radius }
}

// Precompute the (x,y) coordinates of every module ONCE.
const layoutModules = () => {
  const innerCount = MODULES.filter((m) => m.ring === 'inner').length
  const outerCount = MODULES.filter((m) => m.ring === 'outer').length
  return MODULES.map((m) => {
    const radius = m.ring === 'inner' ? R_INNER : R_OUTER
    const step = 360 / (m.ring === 'inner' ? innerCount : outerCount)
    // Offset outer ring by half-step so its nodes fall between the inner
    // ones — looks more organic than concentric spokes.
    const offset = m.ring === 'outer' ? step / 2 : 0
    const angle  = m.slot * step + offset
    const { x, y } = polar(angle, radius)
    return { ...m, x, y, angle }
  })
}

// ── Component ──────────────────────────────────────────────────────────
export default function ArchitectureCanvas({
  viewMode,
  activeId,
  hoverId,
  onSelect,
  onHover,
  reducedMotion = false,
}) {
  const laidOut = useMemo(() => layoutModules(), [])
  const byId    = useMemo(() => new Map(laidOut.map((m) => [m.id, m])), [laidOut])
  const edges   = useMemo(() => buildEdges(), [])

  // Which modules should be visually emphasised right now?  Priority:
  //   1) hovered module + its neighbours dominate (if any)
  //   2) active/selected module + its neighbours
  //   3) fall back to the view-mode highlight set
  const emphasis = useMemo(() => {
    if (hoverId) return relatedIds(hoverId)
    if (activeId) return relatedIds(activeId)
    if (viewMode.highlight === 'all') return null  // null = no dimming
    return new Set(viewMode.highlight)
  }, [hoverId, activeId, viewMode])

  const isEmphasised = (id) => (emphasis ? emphasis.has(id) : true)
  const isEdgeEmphasised = (a, b) => (emphasis ? emphasis.has(a) && emphasis.has(b) : true)

  return (
    <div className="relative w-full h-full">
      <svg
        viewBox={`0 0 ${GEOMETRY.viewBox} ${GEOMETRY.viewBox}`}
        className="w-full h-full select-none"
        role="img"
        aria-label="Interactive Kalki Ai Cloud platform architecture. Use tab and enter to explore modules."
      >
        {/* ─── Reusable gradients / filters ─── */}
        <defs>
          <radialGradient id="coreGlow" cx="50%" cy="50%" r="60%">
            <stop offset="0%"  stopColor="#FFC107" stopOpacity="0.95" />
            <stop offset="40%" stopColor="#FFC107" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#FFC107" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="coreOrb" cx="35%" cy="30%" r="80%">
            <stop offset="0%"  stopColor="#FBF6E9" stopOpacity="0.95" />
            <stop offset="40%" stopColor="#FFC107" stopOpacity="0.90" />
            <stop offset="100%" stopColor="#7a2f00" stopOpacity="1" />
          </radialGradient>
          {Object.entries(TONE_COLORS).map(([key, { hex }]) => (
            <radialGradient id={`nodeGlow-${key}`} key={key} cx="50%" cy="50%" r="60%">
              <stop offset="0%"  stopColor={hex} stopOpacity="0.55" />
              <stop offset="80%" stopColor={hex} stopOpacity="0" />
            </radialGradient>
          ))}
          <linearGradient id="edgeStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="#FFC107" stopOpacity="0.10" />
            <stop offset="50%" stopColor="#FFC107" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0.10" />
          </linearGradient>
          <linearGradient id="edgeStrokeHot" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="#FFC107" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0.90" />
          </linearGradient>
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ─── Ambient rings (decorative) ─── */}
        <g opacity="0.35">
          <circle cx={CENTER} cy={CENTER} r={R_INNER}         stroke="rgba(255,255,255,0.06)" strokeWidth="1" fill="none" strokeDasharray="2 6" />
          <circle cx={CENTER} cy={CENTER} r={R_OUTER}         stroke="rgba(255,255,255,0.05)" strokeWidth="1" fill="none" strokeDasharray="2 8" />
          <circle cx={CENTER} cy={CENTER} r={R_OUTER + 60}    stroke="rgba(255,255,255,0.03)" strokeWidth="1" fill="none" />
        </g>

        {/* ─── Rotating outer decorative ring ─── */}
        <g
          style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
          className={reducedMotion ? '' : 'animate-brand-rot'}
          opacity="0.65"
        >
          {Array.from({ length: 60 }).map((_, i) => {
            const a = (i / 60) * 360
            const { x, y } = polar(a, R_OUTER + 40)
            return <circle key={i} cx={x} cy={y} r={i % 5 === 0 ? 2 : 1} fill="rgba(255,255,255,0.35)" />
          })}
        </g>

        {/* ─── Edges (connection lines) ─── */}
        <g>
          {edges.map(([a, b], i) => {
            const A = byId.get(a), B = byId.get(b)
            if (!A || !B) return null
            const hot = isEdgeEmphasised(a, b)
            const isDirect = hoverId === a || hoverId === b || activeId === a || activeId === b
            // Slight quadratic curve toward core for organic feel.
            const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2
            const dx = mx - CENTER, dy = my - CENTER
            const dist = Math.hypot(dx, dy)
            const pull = 0.18 // curve strength — toward core
            const cx = mx - (dx / dist) * dist * pull
            const cy = my - (dy / dist) * dist * pull
            const d = `M ${A.x} ${A.y} Q ${cx} ${cy} ${B.x} ${B.y}`
            return (
              <g key={i}>
                <path
                  id={`edge-${i}`}
                  d={d}
                  fill="none"
                  stroke={isDirect ? 'url(#edgeStrokeHot)' : 'url(#edgeStroke)'}
                  strokeWidth={isDirect ? 1.8 : 1.1}
                  strokeDasharray={isDirect ? '0' : '4 8'}
                  opacity={hot ? (isDirect ? 1 : 0.75) : 0.12}
                  className={reducedMotion ? '' : (isDirect ? 'brand-dash-fast' : 'brand-dash-slow')}
                  style={{ transition: 'opacity 300ms ease, stroke-width 300ms ease' }}
                />
                {/* Data packet — only render if this edge is emphasised
                    AND animation is allowed.  Keeps SVG light. */}
                {!reducedMotion && hot && (
                  <circle r={isDirect ? 3.2 : 2.2} fill={isDirect ? '#ffd7b0' : '#FFC107'} opacity={isDirect ? 0.95 : 0.65}>
                    <animateMotion
                      dur={`${isDirect ? 2.4 : 4}s`}
                      repeatCount="indefinite"
                      rotate="auto"
                    >
                      <mpath href={`#edge-${i}`} />
                    </animateMotion>
                  </circle>
                )}
              </g>
            )
          })}
        </g>

        {/* ─── Core (Kalki Ai Cloud) ─── */}
        <g style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}>
          <circle
            cx={CENTER} cy={CENTER} r={GEOMETRY.coreRadius + 60}
            fill="url(#coreGlow)"
            opacity="0.55"
            className={reducedMotion ? '' : 'animate-brand-pulse'}
            style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
          />
          <circle cx={CENTER} cy={CENTER} r={GEOMETRY.coreRadius + 20} stroke="rgba(255,193,7,0.35)" strokeWidth="1" fill="none" />
          <circle cx={CENTER} cy={CENTER} r={GEOMETRY.coreRadius} fill="url(#coreOrb)" filter="url(#softGlow)" />
          <circle cx={CENTER - 14} cy={CENTER - 20} r={GEOMETRY.coreRadius * 0.35} fill="rgba(255,255,255,0.35)" opacity="0.5" />
          {/* orbiting particle */}
          {!reducedMotion && (
            <circle r="3" fill="#fff">
              <animateMotion dur="6s" repeatCount="indefinite">
                <mpath href="#coreOrbit" />
              </animateMotion>
            </circle>
          )}
          <path
            id="coreOrbit"
            d={`M ${CENTER + GEOMETRY.coreRadius + 22} ${CENTER}
                a ${GEOMETRY.coreRadius + 22} ${GEOMETRY.coreRadius + 22} 0 1 0 -${(GEOMETRY.coreRadius + 22) * 2} 0
                a ${GEOMETRY.coreRadius + 22} ${GEOMETRY.coreRadius + 22} 0 1 0 ${(GEOMETRY.coreRadius + 22) * 2} 0`}
            fill="none" stroke="none"
          />
          {/* Core label */}
          <text x={CENTER} y={CENTER - 2}   textAnchor="middle" fontSize="16" fontWeight="700" fill="#0a0a0a" style={{ letterSpacing: '0.05em' }}>
            Kalki Ai Cloud
          </text>
          <text x={CENTER} y={CENTER + 16}  textAnchor="middle" fontSize="9"  fontWeight="600" fill="#0a0a0a" style={{ letterSpacing: '0.28em' }}>
            CORE
          </text>
        </g>

        {/* ─── Nodes ─── */}
        {laidOut.map((m) => {
          const tone   = TONE_COLORS[m.tone] || TONE_COLORS.orange
          const radius = m.ring === 'inner' ? GEOMETRY.nodeRadiusInner : GEOMETRY.nodeRadiusOuter
          const emphd  = isEmphasised(m.id)
          const focused = activeId === m.id || hoverId === m.id
          const scale  = focused ? 1.18 : 1
          const dim    = !emphd

          return (
            <g
              key={m.id}
              className="cursor-pointer focus:outline-none"
              tabIndex={0}
              role="button"
              aria-label={`${m.name} — ${m.purpose}`}
              aria-pressed={activeId === m.id}
              onMouseEnter={() => onHover(m.id)}
              onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(m.id)}
              onBlur={() => onHover(null)}
              onClick={() => onSelect(m.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(m.id) } }}
              style={{
                transform: `translate(${m.x}px, ${m.y}px) scale(${scale})`,
                transformOrigin: `${m.x}px ${m.y}px`,
                transformBox: 'fill-box',
                transition: 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease',
                opacity: dim ? 0.28 : 1,
              }}
            >
              {/* halo */}
              <circle cx="0" cy="0" r={radius + 18} fill={`url(#nodeGlow-${m.tone})`} opacity={focused ? 0.9 : 0.55} />
              {/* body */}
              <circle
                cx="0" cy="0" r={radius}
                fill="rgba(10,10,12,0.85)"
                stroke={focused ? tone.hex : `rgba(${tone.rgb}, 0.55)`}
                strokeWidth={focused ? 2 : 1.1}
                style={{ transition: 'stroke 200ms ease' }}
              />
              {/* subtle inner tint */}
              <circle cx="0" cy="0" r={radius - 3} fill={`rgba(${tone.rgb}, 0.06)`} />
              {/* icon via foreignObject (lucide is React) */}
              <foreignObject x={-radius * 0.55} y={-radius * 0.55} width={radius * 1.1} height={radius * 1.1}>
                <div className="flex items-center justify-center w-full h-full" style={{ color: tone.hex }}>
                  <NodeIcon iconKey={m.iconKey} />
                </div>
              </foreignObject>
              {/* label — placed just below the node */}
              <text
                x="0" y={radius + 15}
                textAnchor="middle"
                fontSize={m.ring === 'inner' ? 13 : 12}
                fontWeight="600"
                fill={focused ? '#fff' : 'rgba(255,255,255,0.86)'}
                style={{ letterSpacing: '0.01em' }}
              >
                {m.name}
              </text>
            </g>
          )
        })}
      </svg>

      {/* Dash animation classes — Tailwind can't produce keyframe-based
          strokeDashoffset animations directly, so we ship two tiny CSS
          classes here.  They reuse the `brand-dash` @keyframes registered
          in tailwind.config.js. */}
      <style>{`
        .brand-dash-slow { animation: brand-dash 2.6s linear infinite }
        .brand-dash-fast { animation: brand-dash 1.4s linear infinite }
      `}</style>
    </div>
  )
}

// A tiny wrapper that resolves the lucide icon by key.
function NodeIcon({ iconKey }) {
  const C = ICONS[iconKey] || Sparkles
  return <C strokeWidth={1.8} style={{ width: '55%', height: '55%' }} />
}
